import { Component, Element, Event, EventEmitter, h, Host, Listen, Method, Prop, State, Watch } from "@stencil/core";
import { ModalStatusEnum, ModalTransitionStatusType } from "./modal-status";
import { ModalMultiIndicator } from "./modal-multi-indicator";
import { getModalByName, getPriority } from "./modal-utils";

@Component({
    tag: "ux-modal",
    styleUrl: "modal.scss",
    shadow: false
})
export class Modal {
    @Element() domNode!: HTMLUxModalElement;

    /**
     * Set the name of the modal
     */
    @Prop({ reflect: true }) readonly name: string = "";

    /**
     * Set the priority of the modal as an integer between 1 and 10 - lower numbers have higher importance
     */
    @Prop() readonly priority: number = 5;

    /**
     * The label of the close button
     */
    @Prop() readonly closeButtonText: string = "";

    /**
     * Disables the close button in the top corner
     */
    @Prop() readonly noCloseButton: boolean = false;

    /**
     * If present, uxModalClose event is emitted when the escape key is pressed
     */
    @Prop() readonly closeOnEscKey: boolean = false;

    /**
     * If present, uxModalClose event is emitted when the backdrop is clicked
     */
    @Prop() readonly closeOnBackdropClick: boolean = false;

    /**
     * Reloads the page when the modal is closed
     */
    @Prop() readonly reloadOnClose: boolean = false;

    /**
     * At which vertical position should the modal be docked
     */
    @Prop() readonly stickyPosition: "center" | "bottom" | "top" = "center";

    /**
     * Boolean to indicate if the modal is currently opened
     */
    @Prop({ mutable: true, reflect: true }) opened = false;

    /**
     * If set, it will eliminate the top gutter in the modal
     */
    @Prop() readonly noGutterTop: boolean = false;

    /**
     * Controls whether the body scroll lock is disabled when the modal is open
     */
    @Prop() readonly disableScrollLock: boolean = false;

    /**
     * The modal transition status
     */
    @State() status: ModalTransitionStatusType = ModalStatusEnum.CLOSED;

    /**
     * List of multiple opened Modals that should be displayed via the ModalManager
     */
    @State() multipleModals: string[] | null = null;

    /**
     * List of multiple opened Modals that should be displayed via the ModalManager
     */
    @State() multipleModalsCurrent: string | null = null;

    /**
     * Emitted when the modal is closed
     */
    @Event({ eventName: "uxModalClose", bubbles: true, cancelable: true })
    modalClosed!: EventEmitter<string>;

    /**
     * Emitted when the modal is opened
     */
    @Event({ eventName: "uxModalOpen", bubbles: true, cancelable: true })
    modalOpened!: EventEmitter<string>;

    /* eslint-disable */
    private transitionTimeout: any | null = null;

    private bodyScrollLock(isLocked: boolean): void {
        if (isLocked && !this.disableScrollLock) {
            document.body.classList.add("ux-body-scroll-lock");
        } else {
            document.body.classList.remove("ux-body-scroll-lock");
        }
    }

    /**
     * Triggers modal closing
     */
    @Method()
    async triggerClose(): Promise<void> {
        const evt = this.modalClosed.emit(this.name);
        if (!evt.defaultPrevented) {
            this.opened = false;
            if (this.reloadOnClose) {
                window.setTimeout(() => window.location.reload(), 250);
            }
        }
    }

    /**
     * Triggers modal opening
     */
    @Method()
    async triggerOpen(): Promise<void> {
        const evt = this.modalOpened.emit(this.name);
        if (!evt.defaultPrevented) {
            this.opened = true;
        }
    }

    /**
     * Listen for other closed modals
     */
    @Listen("uxModalClose", { target: "window" })
    onOtherClosedModal(e: CustomEvent): void {
        this.setMultipleModalsCurrent(this.getOpenMultipleModals(e.detail));
        if (this.multipleModalsCurrent === this.name && this.opened) {
            this.showModal();
        }
        this.bodyScrollLock(false);
    }

    /**
     * Listen for other opened modals
     */
    @Listen("uxModalOpen", { target: "window" })
    onOtherOpenedModal(e: CustomEvent): void {
        const openedModalName = e.detail;
        const isAlreadyInCue = this.getOpenMultipleModals().includes(openedModalName);
        if (openedModalName === this.name || isAlreadyInCue) {
            return;
        }
        // @ts-expect-error Check for typing
        this.multipleModals.push(openedModalName);
        this.sortMultipleModals();
        this.setMultipleModalsCurrent(this.getOpenMultipleModals());
        this.bodyScrollLock(true);
    }

    /**
     * Listen for opened state
     */
    @Watch("opened")
    setModalStatus() {
        if (this.transitionTimeout !== null) {
            clearTimeout(this.transitionTimeout);
        }

        if (!this.opened) {
            if (this.status === ModalStatusEnum.CLOSED) {
                return;
            }
            this.status = ModalStatusEnum.CLOSING;
            this.transitionTimeout = setTimeout(() => {
                this.status = ModalStatusEnum.CLOSED;
            }, 350);
            return;
        }

        if (this.status === ModalStatusEnum.OPENED) {
            return;
        }
        this.initMultipleModals();
        // @ts-expect-error Check for typing
        if (this.multipleModals.length > 0 && !this.multipleModals.includes(this.name)) {
            // @ts-expect-error Check for typing
            this.multipleModals.push(this.name);
        }
        // @ts-expect-error Check for typing
        if (this.multipleModals.length < 2 || this.multipleModalsCurrent === this.name) {
            this.showModal();
        }
    }

    private initMultipleModals(): void {
        if (this.multipleModals === null) {
            const initiallyOpenedModals: HTMLUxModalElement[] = Array.from(document.querySelectorAll("ux-modal[opened]"));
            this.multipleModals = initiallyOpenedModals.map((modal) => modal.name);
        }
        this.sortMultipleModals();
        this.setMultipleModalsCurrent(this.getOpenMultipleModals());
    }

    private setMultipleModalsCurrent(openedModals: string[]): void {
        if (this.multipleModals === null) {
            return;
        }
        this.cleanupMultipleModals(openedModals);
        if (this.multipleModals.length > 0) {
            this.multipleModalsCurrent = openedModals[0];
        }
    }

    // @ts-expect-error Check for typing
    private getOpenMultipleModals(exclude: string = null): string[] {
        // @ts-expect-error Check for typing
        return this.multipleModals.filter((name) => {
            const modal = getModalByName(name);
            if (modal === null || name === exclude) {
                return false;
            }
            return modal.opened;
        });
    }

    private cleanupMultipleModals(openModals: string[]): void {
        if (openModals.length === 0) {
            this.multipleModals = [];
            this.multipleModalsCurrent = null;
            return;
        }
    }

    private sortMultipleModals(): void {
        if (this.multipleModals === null) {
            return;
        }
        this.multipleModals.sort((a, b) => getPriority(a) - getPriority(b));
    }

    private showModal(): void {
        this.status = ModalStatusEnum.OPENING;
        this.transitionTimeout = setTimeout(() => {
            this.status = ModalStatusEnum.OPENED;
            this.bodyScrollLock(true);
        }, 100);
    }

    /**
     * Listen for escape key
     */
    @Listen("keydown", { target: "window" })
    handleKeyDown(event: KeyboardEvent) {
        if (this.closeOnEscKey && event.key === "Escape") {
            this.triggerClose();
        }
    }

    private triggerElementClickHandler = (e: MouseEvent) => {
        const targetElement = (e.target as HTMLElement).closest("[data-trigger-modal]");
        if (targetElement === null) {
            return;
        }
        const triggers: string[][] = (targetElement.getAttribute("data-trigger-modal") || "")
            .split(";")
            .map((string) => string.split(","));
        triggers.forEach((trigger) => {
            if (trigger.length > 1) {
                switch (trigger[0].valueOf()) {
                    case "open":
                        if (trigger[1] === this.name) {
                            this.triggerOpen();
                        }
                        break;
                    case "close":
                        if (trigger[1] === undefined || trigger[1] === this.name) {
                            this.triggerClose();
                        }
                        break;
                    default:
                        break;
                }
            }
        });
    };

    componentWillLoad() {
        this.setModalStatus();
        this.initMultipleModals();
        this.onOtherClosedModal.bind(this);
        this.onOtherOpenedModal.bind(this);
    }

    componentDidLoad() {
        document.addEventListener("click", this.triggerElementClickHandler);
    }

    disconnectedCallback() {
        document.removeEventListener("click", this.triggerElementClickHandler);
    }

    render() {
        const handleClickClose = () => {
            this.triggerClose();
        };
        // @ts-expect-error Check for typing
        const currentMultiModal = this.multipleModals.indexOf(this.name) + 1;
        return (
            <Host
                class={{
                    ["ux-modal"]: true,
                    [`ux-modal--${this.status}`]: true,
                    [`ux-modal--sticky-${this.stickyPosition}`]: true,
                    [`ux-modal--no-gutter-top`]: this.noGutterTop
                }}
            >
                {/* @ts-expect-error Check for typing */}
                <div class="ux-modal__backdrop" onClick={this.closeOnBackdropClick ? handleClickClose : null} />
                <div class="ux-modal__outer" role="dialog" aria-modal={true}>
                    <div class="ux-modal__inner">
                        {/* @ts-expect-error Check for typing */}
                        <ModalMultiIndicator current={currentMultiModal} total={this.multipleModals.length} />
                        <div class="ux-modal__head">
                            {!this.noCloseButton && (
                                <button
                                    type="button"
                                    class="ux-modal__close"
                                    aria-label={this.closeButtonText}
                                    title={this.closeButtonText}
                                    onClick={handleClickClose}
                                >
                                    <ux-icon name="cross" weight="normal" aria-hidden="true" />
                                </button>
                            )}
                        </div>
                        <div class="ux-modal__slot">
                            <slot />
                        </div>
                    </div>
                </div>
            </Host>
        );
    }
}
